import React from 'react';
import { Container, Row, Col, Button, Card } from 'react-bootstrap';
import {
  BoltIcon,
  ArrowRightIcon,
  CheckIcon,
  CubeTransparentIcon,
  RocketLaunchIcon,
} from '@heroicons/react/24/solid';
import PublicNavbar from './common/PublicNavbar';
import PublicFooter from './common/PublicFooter';
import { useHomepage } from './hooks/useHomepage';

const Homepage: React.FC = () => {
  const { navigate, content, features, steps } = useHomepage();

  return (
    <div style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}>
      {/* ─── Navbar ─── */}
      <PublicNavbar />

      {/* ─── Hero Section ─── */}
      <section
        style={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 40%, #3b82f6 100%)',
          minHeight: 'min(100vh, 920px)',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden',
          padding: '5.5rem 0 3.5rem',
        }}
      >
        {/* Background pattern */}
        <div style={{
          position: 'absolute', inset: 0, opacity: 0.05,
          backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
          backgroundSize: '40px 40px',
        }} />

        <Container style={{ position: 'relative', zIndex: 1 }}>
          <Row className="align-items-center">
            <Col lg={8} className="text-white mb-5 mb-lg-0">
              <div
                style={{
                  display: 'inline-block',
                  background: 'rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '999px',
                  padding: '6px 16px',
                  fontSize: '0.85rem',
                  fontWeight: 500,
                  marginBottom: '1.5rem',
                  border: '1px solid rgba(255,255,255,0.15)',
                }}
              >
                <BoltIcon style={{ width: '14px', height: '14px', marginRight: '6px', color: '#fbbf24' }} />
                {content.hero_badge}
              </div>

              <h1 style={{
                fontSize: 'clamp(2rem, 4.4vw, 3.05rem)',
                fontWeight: 800,
                lineHeight: 1.14,
                marginBottom: '1.25rem',
                letterSpacing: '-1px',
                maxWidth: '760px',
              }}>
                {content.hero_title_line1}{' '}
                <span style={{
                  background: 'linear-gradient(135deg, #60a5fa, #a78bfa)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}>
                  {content.hero_title_highlight}
                </span>
                {' '}{content.hero_title_line3}
              </h1>

              <p style={{
                fontSize: 'clamp(1rem, 2vw, 1.2rem)',
                color: 'rgba(255,255,255,0.7)',
                maxWidth: '620px',
                lineHeight: 1.6,
                marginBottom: '2rem',
              }}>
                {content.hero_description}
              </p>

              <div className="d-flex gap-3 flex-wrap">
                <Button
                  size="lg"
                  variant="link"
                  onClick={() => navigate('/register')}
                  className="d-flex align-items-center gap-2"
                  style={{
                    borderRadius: '14px',
                    padding: '12px 24px',
                    fontWeight: 700,
                    fontSize: '0.96rem',
                    background: 'white',
                    color: '#1e3a8a',
                    border: 'none',
                    boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
                    textDecoration: 'none',
                  }}
                >
                  <RocketLaunchIcon style={{ width: '20px', height: '20px' }} />
                  {content.hero_cta_primary}
                </Button>
                <Button
                  size="lg"
                  variant="link"
                  onClick={() => navigate('/pricing')}
                  className="d-flex align-items-center gap-2"
                  style={{
                    borderRadius: '14px',
                    padding: '12px 24px',
                    fontWeight: 600,
                    fontSize: '0.96rem',
                    border: '2px solid rgba(255,255,255,0.4)',
                    color: 'white',
                    background: 'transparent',
                    textDecoration: 'none',
                  }}
                >
                  {content.hero_cta_secondary}
                </Button>
                <Button
                  size="lg"
                  variant="link"
                  onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
                  className="d-flex align-items-center gap-2"
                  style={{
                    borderRadius: '14px',
                    padding: '12px 24px',
                    fontWeight: 600,
                    fontSize: '0.96rem',
                    border: '2px solid rgba(255,255,255,0.4)',
                    color: 'white',
                    background: 'transparent',
                    textDecoration: 'none',
                  }}
                >
                  {content.hero_cta_tertiary}
                  <ArrowRightIcon style={{ width: '16px', height: '16px' }} />
                </Button>
              </div>

              {/* Trust badges */}
              <div className="d-flex align-items-center gap-4 mt-4 flex-wrap" style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>
                {(content.hero_trust_badges || []).map((badge, i) => (
                  <span key={i} className="d-flex align-items-center gap-1">
                    <CheckIcon style={{ width: '14px', height: '14px', color: '#34d399' }} /> {badge}
                  </span>
                ))}
              </div>
            </Col>

            <Col lg={4} className="d-none d-lg-block">
              {/* Dashboard preview mockup */}
              <div style={{
                background: 'rgba(255,255,255,0.08)',
                backdropFilter: 'blur(20px)',
                borderRadius: '20px',
                border: '1px solid rgba(255,255,255,0.12)',
                padding: '24px',
                boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              }}>
                <div className="d-flex gap-2 mb-3">
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#ef4444' }} />
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#f59e0b' }} />
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#22c55e' }} />
                </div>
                {/* Mock stats */}
                <Row className="g-2 mb-3">
                  {[
                    { label: 'Revenue', value: '$12,450', color: '#10b981' },
                    { label: 'Orders', value: '156', color: '#3b82f6' },
                    { label: 'Clients', value: '24', color: '#8b5cf6' },
                  ].map((s) => (
                    <Col key={s.label} xs={4}>
                      <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: '12px', padding: '12px', textAlign: 'center' }}>
                        <div style={{ color: s.color, fontSize: '1.2rem', fontWeight: 700 }}>{s.value}</div>
                        <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.7rem' }}>{s.label}</div>
                      </div>
                    </Col>
                  ))}
                </Row>
                {/* Mock chart bars */}
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', height: '120px', padding: '0 4px' }}>
                  {[40, 65, 45, 80, 55, 90, 70, 85, 60, 95, 75, 88].map((h, i) => (
                    <div
                      key={`bar-${i}`}
                      style={{
                        flex: 1,
                        height: `${h}%`,
                        borderRadius: '4px 4px 0 0',
                        background: `linear-gradient(180deg, rgba(59,130,246,0.8) 0%, rgba(59,130,246,0.3) 100%)`,
                      }}
                    />
                  ))}
                </div>
                <div className="d-flex justify-content-between mt-2" style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.3)' }}>
                  <span>Jan</span><span>Feb</span><span>Mar</span><span>Apr</span><span>May</span><span>Jun</span>
                  <span>Jul</span><span>Aug</span><span>Sep</span><span>Oct</span><span>Nov</span><span>Dec</span>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ─── Features Section ─── */}
      <section id="features" style={{ padding: '100px 0', background: '#f8fafc' }}>
        <Container>
          <div className="text-center mb-5">
            <div style={{
              display: 'inline-block',
              background: '#eff6ff',
              color: '#1e3a8a',
              borderRadius: '999px',
              padding: '6px 16px',
              fontSize: '0.85rem',
              fontWeight: 600,
              marginBottom: '1rem',
            }}>
              Features
            </div>
            <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 2.5rem)', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.5px' }}>
              Everything you need to run
              <br />
              your wholesale operation
            </h2>
            <p style={{ color: '#64748b', fontSize: '1.1rem', maxWidth: '600px', margin: '1rem auto 0' }}>
              From order management to financial reporting, Sourdoughsy gives you the tools to scale your bakery business.
            </p>
          </div>

          <Row className="g-4">
            {features.map((f) => (
              <Col key={f.title} md={6} lg={4}>
                <Card
                  className="h-100 border-0"
                  style={{
                    borderRadius: '16px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                    transition: 'all 0.3s ease',
                    cursor: 'default',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.04)';
                  }}
                >
                  <Card.Body className="p-4">
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '12px',
                      background: `${f.color}15`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '1rem',
                    }}>
                      <f.icon style={{ width: '24px', height: '24px', color: f.color }} />
                    </div>
                    <h5 style={{ fontWeight: 700, color: '#0f172a', marginBottom: '0.5rem' }}>{f.title}</h5>
                    <p style={{ color: '#64748b', fontSize: '0.95rem', lineHeight: 1.6, marginBottom: 0 }}>{f.desc}</p>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ─── How It Works ─── */}
      <section style={{ padding: '100px 0', background: 'white' }}>
        <Container>
          <div className="text-center mb-5">
            <div style={{
              display: 'inline-block',
              background: '#f0fdf4',
              color: '#065f46',
              borderRadius: '999px',
              padding: '6px 16px',
              fontSize: '0.85rem',
              fontWeight: 600,
              marginBottom: '1rem',
            }}>
              {content.steps_badge}
            </div>
            <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 2.5rem)', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.5px' }}>
              {content.steps_title}
            </h2>
          </div>

          <Row className="g-4 justify-content-center">
            {steps.map((step) => (
              <Col key={step.num} md={4}>
                <div className="text-center">
                  <div style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #1e3a8a, #3b82f6)',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 1.25rem',
                    fontSize: '1.2rem',
                    fontWeight: 800,
                    boxShadow: '0 8px 24px rgba(30,58,138,0.25)',
                  }}>
                    {step.num}
                  </div>
                  <h5 style={{ fontWeight: 700, color: '#0f172a', marginBottom: '0.5rem' }}>{step.title}</h5>
                  <p style={{ color: '#64748b', fontSize: '0.95rem', lineHeight: 1.6 }}>{step.desc}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ─── Branded Portals Highlight ─── */}
      <section style={{ padding: '100px 0', background: 'linear-gradient(135deg, #065f46 0%, #047857 50%, #10b981 100%)', color: 'white' }}>
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="mb-4 mb-lg-0">
              <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 2.5rem)', fontWeight: 800, lineHeight: 1.2, letterSpacing: '-0.5px' }}>
                {content.portals_title}
              </h2>
              <p style={{ fontSize: '1.1rem', color: 'rgba(255,255,255,0.8)', marginTop: '1rem', lineHeight: 1.7 }}>
                {content.portals_description}
              </p>
              <div className="mt-4">
                {(content.portals_features || []).map((item, i) => (
                  <div key={i} className="d-flex align-items-center gap-2 mb-2" style={{ fontSize: '1rem' }}>
                    <CheckIcon style={{ width: '18px', height: '18px', color: '#34d399' }} />
                    {item}
                  </div>
                ))}
              </div>
            </Col>
            <Col lg={6} className="d-flex justify-content-center">
              {/* Portal mockup */}
              <div style={{
                background: 'white',
                borderRadius: '20px',
                padding: '32px',
                maxWidth: '360px',
                width: '100%',
                boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                textAlign: 'center',
              }}>
                <div style={{
                  width: '70px',
                  height: '70px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #065f46, #10b981)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem',
                }}>
                  <CubeTransparentIcon style={{ width: '36px', height: '36px', color: 'white' }} />
                </div>
                <h5 style={{ fontWeight: 700, color: '#065f46', marginBottom: '4px' }}>{content.portals_mockup_name}</h5>
                <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginBottom: '1.5rem' }}>{content.portals_mockup_subtitle}</p>
                <div style={{ background: '#f1f5f9', borderRadius: '10px', padding: '12px', marginBottom: '12px', textAlign: 'left', color: '#94a3b8', fontSize: '0.9rem' }}>
                  Username
                </div>
                <div style={{ background: '#f1f5f9', borderRadius: '10px', padding: '12px', marginBottom: '1.25rem', textAlign: 'left', color: '#94a3b8', fontSize: '0.9rem' }}>
                  Password
                </div>
                <div style={{
                  background: 'linear-gradient(135deg, #065f46, #10b981)',
                  borderRadius: '10px',
                  padding: '12px',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                }}>
                  Access Dashboard
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ─── CTA Section ─── */}
      <section style={{ padding: '100px 0', background: '#0f172a', textAlign: 'center' }}>
        <Container>
          <h2 style={{
            fontSize: 'clamp(1.8rem, 4vw, 2.8rem)',
            fontWeight: 800,
            color: 'white',
            lineHeight: 1.2,
            letterSpacing: '-0.5px',
            marginBottom: '1rem',
          }}>
            {content.cta_title_line1}
            <br />
            <span style={{
              background: 'linear-gradient(135deg, #60a5fa, #a78bfa)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}>
              {content.cta_title_highlight}
            </span>
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '1.1rem', maxWidth: '500px', margin: '0 auto 2rem' }}>
            {content.cta_description}
          </p>
          <Button
            size="lg"
            variant="link"
            onClick={() => navigate('/register')}
            className="d-inline-flex align-items-center gap-2"
            style={{
              borderRadius: '14px',
              padding: '16px 40px',
              fontWeight: 700,
              fontSize: '1.05rem',
              background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
              color: 'white',
              border: 'none',
              boxShadow: '0 8px 30px rgba(59,130,246,0.3)',
              textDecoration: 'none',
            }}
          >
            <RocketLaunchIcon style={{ width: '20px', height: '20px' }} />
            {content.cta_button_text}
          </Button>
        </Container>
      </section>

      {/* ─── Footer ─── */}
      <PublicFooter />
    </div>
  );
};

export default Homepage;
